import { useEffect, useState } from 'react';
import { Dispatch, bindActionCreators } from 'redux';
import { FormattedMessage, injectIntl, useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { RootState } from '../../reducers';
import * as actions from '../../actions/generalActions';
import { axiosCaller } from '../../utils/apiCaller';
import { toast } from 'react-toastify';
import { t } from '../../i18n';
import MikaTranslationsModal from '../../components/MikaTranslationsModal/MikaTranslationsModal';
import './offerSettings.scss';

interface Category {
    id: number;
    name: string;
    name_translations?: Record<string, string>;
    is_default: boolean;
    is_protected: boolean;
    sort_order: number;
}

interface Props {
    actions: any;
    buttonLoader: boolean;
}

const ReturnCategories = (props: Props) => {
    const intl = useIntl();
    const fm = (id: string, defaultMessage: string) => intl.formatMessage({ id, defaultMessage });
    const [categories, setCategories] = useState<Category[]>([]);
    const [newName, setNewName] = useState('');
    const [editingId, setEditingId] = useState<number | null>(null);
    const [editingName, setEditingName] = useState('');
    const [loading, setLoading] = useState(true);
    // Translation modal: when non-null, we're editing the full per-locale map
    // of the referenced category. Closed by setting back to null.
    const [translatingCategory, setTranslatingCategory] = useState<Category | null>(null);

    useEffect(() => {
        loadCategories();
    }, []);

    const loadCategories = () => {
        setLoading(true);
        axiosCaller('GET', 'return-categories').then((res: any) => {
            setCategories(res?.data || []);
            setLoading(false);
        }).catch(() => {
            setLoading(false);
        });
    };

    const addCategory = () => {
        if (!newName.trim()) {
            toast.error(t('admin.returnCategories.toastFillName'));
            return;
        }
        axiosCaller('POST', 'admin/return-categories', { name: newName }).then(() => {
            toast.success(t('admin.returnCategories.toastAdded'));
            setNewName('');
            loadCategories();
        }).catch(() => {
            toast.error(t('admin.toast.somethingWentWrong'));
        });
    };

    const startEdit = (cat: Category) => {
        setEditingId(cat.id);
        setEditingName(cat.name);
    };

    const saveEdit = () => {
        if (!editingName.trim()) {
            toast.error(t('admin.returnCategories.toastNameEmpty'));
            return;
        }
        axiosCaller('PATCH', `admin/return-categories/${editingId}`, { name: editingName }).then(() => {
            toast.success(t('admin.returnCategories.toastUpdated'));
            setEditingId(null);
            setEditingName('');
            loadCategories();
        }).catch(() => {
            toast.error(t('admin.toast.somethingWentWrong'));
        });
    };

    const cancelEdit = () => {
        setEditingId(null);
        setEditingName('');
    };

    const saveTranslations = async (translations: Record<string, string>) => {
        if (!translatingCategory) return;
        try {
            await axiosCaller(
                'PUT',
                `admin/return-categories/${translatingCategory.id}/translations`,
                { translations },
            );
            toast.success(intl.formatMessage({
                id: 'admin.returnCategories.toastTranslationsSaved',
                defaultMessage: 'Përkthimet u ruajtën',
            }));
            setTranslatingCategory(null);
            loadCategories();
        } catch (e) {
            toast.error(t('admin.toast.somethingWentWrong'));
        }
    };

    const deleteCategory = (cat: Category) => {
        if (cat.is_protected) {
            toast.error(t('admin.returnCategories.toastCantDelete'));
            return;
        }
        const confirmMsg = intl.formatMessage(
            { id: 'admin.returnCategories.confirmDelete', defaultMessage: 'A jeni të sigurt që dëshironi ta fshini kategorinë {name}?' },
            { name: '"' + cat.name + '"' }
        );
        if (!window.confirm(confirmMsg)) return;
        axiosCaller('DELETE', `admin/return-categories/${cat.id}`).then(() => {
            toast.success(t('admin.returnCategories.toastDeleted'));
            loadCategories();
        }).catch(() => {
            toast.error(t('admin.toast.somethingWentWrong'));
        });
    };

    return (
        <div style={{ padding: 20, width: '100%' }}>
            <div className="os-page">
                <div className="os-page-header">
                    <div>
                        <h1 className="os-page-title"><FormattedMessage id="admin.returnCategories.pageTitle" defaultMessage="Kategoritë e kthimit" /></h1>
                        <p className="os-page-subtitle"><FormattedMessage id="admin.returnCategories.pageSubtitle" defaultMessage="Menaxhoni kategoritë që përdoren kur ktheni procese në prodhim" /></p>
                    </div>
                </div>

                <div className="os-card">
                    <div className="os-card-header">
                        <div>
                            <h2><FormattedMessage id="admin.returnCategories.cardTitle" defaultMessage="Kategoritë" /></h2>
                            <p className="os-card-desc"><FormattedMessage id="admin.returnCategories.cardDesc" defaultMessage="Këto kategori shfaqen si opsion kur një administrator kthen një proces në prodhim" /></p>
                        </div>
                    </div>
                    <div className="os-card-body">
                        {loading ? (
                            <p className="os-empty-text"><FormattedMessage id="common.loading" defaultMessage="Duke ngarkuar..." /></p>
                        ) : (
                            <>
                                <div className="os-field-pills">
                                    {categories.map((cat) => (
                                        <div key={cat.id} className="os-field-pill">
                                            {editingId === cat.id ? (
                                                <div className="os-pill-edit">
                                                    <input
                                                        type="text"
                                                        value={editingName}
                                                        onChange={(e) => setEditingName(e.target.value)}
                                                        onKeyDown={(e) => e.key === 'Enter' && saveEdit()}
                                                        autoFocus
                                                    />
                                                    <button type="button" className="os-pill-action save" onClick={saveEdit} title={fm('common.action.save', 'Ruaj')}>
                                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                                                    </button>
                                                    <button type="button" className="os-pill-action cancel" onClick={cancelEdit} title={fm('common.action.cancel', 'Anulo')}>
                                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                                                    </button>
                                                </div>
                                            ) : (
                                                <>
                                                    <span className="os-pill-name">
                                                        {cat.name}
                                                        {cat.is_default && (
                                                            <span style={{ fontSize: 9, color: '#94a3b8', marginLeft: 4, fontWeight: 400 }}><FormattedMessage id="admin.returnCategories.defaultBadge" defaultMessage="(default)" /></span>
                                                        )}
                                                    </span>
                                                    <button type="button" className="os-pill-action edit" onClick={() => startEdit(cat)} title={fm('common.action.edit', 'Ndrysho')}>
                                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                                                    </button>
                                                    {/* Translations: open the per-locale editor modal. Globe icon
                                                        is the canonical "this is multilingual" cue across modern
                                                        admin UIs (GitHub, Linear, Notion, Vercel, etc.). */}
                                                    <button type="button" className="os-pill-action edit" onClick={() => setTranslatingCategory(cat)} title={fm('common.action.translations', 'Përkthime')}>
                                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                            <circle cx="12" cy="12" r="10"/>
                                                            <line x1="2" y1="12" x2="22" y2="12"/>
                                                            <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                                                        </svg>
                                                    </button>
                                                    {!cat.is_protected && (
                                                        <button type="button" className="os-pill-action delete" onClick={() => deleteCategory(cat)} title={fm('common.action.delete', 'Fshi')}>
                                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                                                        </button>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    ))}
                                </div>

                                <div className="os-add-field">
                                    <input
                                        type="text"
                                        value={newName}
                                        onChange={(e) => setNewName(e.target.value)}
                                        placeholder={fm('admin.returnCategories.placeholder', 'Emri i kategorisë së re...')}
                                        onKeyDown={(e) => e.key === 'Enter' && addCategory()}
                                    />
                                    <button type="button" className="os-add-btn" onClick={addCategory} disabled={!newName.trim()}>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                                        <FormattedMessage id="admin.returnCategories.btnAdd" defaultMessage="Shto" />
                                    </button>
                                </div>

                                {categories.length === 0 && (
                                    <p className="os-empty-text"><FormattedMessage id="admin.returnCategories.empty" defaultMessage="Nuk ka kategori. Shtoni kategorinë e parë me formën më sipër." /></p>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>

            <MikaTranslationsModal
                isOpen={!!translatingCategory}
                onClose={() => setTranslatingCategory(null)}
                title={fm('admin.returnCategories.translationsTitle', 'Përkthime: {name}').replace('{name}', translatingCategory?.name || '')}
                initialTranslations={translatingCategory?.name_translations || {}}
                onSave={saveTranslations}
            />
        </div>
    );
};

function mapStateToProps(state: RootState, ownProps: any) {
    return { ...ownProps, ...state.app };
}

function mapDispatchToProps(dispatch: Dispatch<actions.ACTION>) {
    return {
        actions: bindActionCreators(actions as any, dispatch),
    };
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(injectIntl(ReturnCategories as any)));
